<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-min-h-0">
    <!-- Panel heading -->
    <div v-if="showHeading" class="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-shrink-0">
      <div class="tw-flex tw-items-center tw-gap-2">
        <FileStack class="tw-h-4 tw-w-4 tw-text-secondary-500 dark:tw-text-neutral-400" />
        <h2
          class="tw-font-heading tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary-700 dark:tw-text-neutral-300"
        >
          {{ t('orders.documents.documents') }}
        </h2>
        <span
          v-if="displayFiles.length"
          class="tw-inline-flex tw-h-5 tw-min-w-[1.25rem] tw-items-center tw-justify-center tw-rounded-full tw-bg-surface-200/70 tw-px-1.5 tw-text-[0.7rem] tw-font-semibold tw-text-secondary-600 dark:tw-bg-neutral-800 dark:tw-text-neutral-300"
        >
          {{ displayFiles.length }}
        </span>
      </div>
    </div>

    <AlascoDocumentTabs
      v-if="documentTabs.length"
      v-model="activeTab"
      :tabs="documentTabs"
      :show-add="editable && documentTabs.length < maxFiles"
      :add-label="t('orders.documents.add_document')"
      class="tw-flex-1"
      @add="openFilePicker"
      @close="handleTabClose"
    >
      <template v-for="file in displayFiles" :key="String(file.id)" #[String(file.id)]>
        <!-- Viewer toolbar -->
        <div
          v-if="activeFile"
          class="tw-mb-3 tw-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-xl tw-border tw-border-surface-200 tw-bg-white tw-px-1.5 tw-py-1 tw-shadow-[0_1px_2px_rgba(20,21,36,0.05)] dark:tw-border-neutral-800 dark:tw-bg-neutral-950"
        >
          <Button
            v-if="activeFileUrl"
            variant="ghost"
            size="icon"
            class="tw-h-8 tw-w-8 tw-text-secondary-600 hover:tw-text-secondary-900 dark:tw-text-neutral-400"
            as="a"
            :href="activeFileUrl"
            target="_blank"
            download
            :title="t('common.forms.download')"
          >
            <Download class="tw-h-4 tw-w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="tw-h-8 tw-w-8 tw-text-secondary-600 hover:tw-text-secondary-900 dark:tw-text-neutral-400"
            :title="t('orders.documents.fullscreen')"
            @click="toggleFullscreen"
          >
            <Maximize2 class="tw-h-4 tw-w-4" />
          </Button>

          <span
            class="tw-ml-auto tw-truncate tw-px-2 tw-text-xs tw-text-surface-400 dark:tw-text-neutral-500"
          >
            {{ activeFile.file_name }}
          </span>
        </div>

        <div
          ref="previewContainerRef"
          class="tw-flex-1 tw-min-h-[360px] tw-overflow-hidden tw-rounded-xl tw-border tw-border-surface-200 dark:tw-border-neutral-800 tw-bg-surface-100/70 dark:tw-bg-neutral-900/50"
        >
          <!-- PDF: native browser viewer (its own page nav / zoom / rotate / print controls) -->
          <iframe
            v-if="isPdf(file)"
            :src="getPreviewUrl(file)"
            :title="file.file_name || 'PDF preview'"
            class="tw-h-full tw-w-full tw-border-0"
          />

          <!-- Image: fit-to-view, never upscaled. Small images sit centered at natural size. -->
          <div
            v-else
            class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-overflow-auto tw-p-3"
          >
            <img
              :src="getPreviewUrl(file)"
              :alt="file.file_name || 'preview'"
              class="tw-max-h-full tw-max-w-full tw-object-contain"
            />
          </div>
        </div>
      </template>
    </AlascoDocumentTabs>

    <div v-else class="tw-flex-1 tw-min-h-[360px]">
      <AttachmentDropzone
        v-if="editable"
        :current-count="0"
        :disabled="disabled || uploading"
        @add="files => emit('add', files)"
        @error="message => emit('error', message)"
      />
      <div
        v-else
        class="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-surface-200 tw-bg-white/40 tw-text-center tw-py-12 dark:tw-border-neutral-800 dark:tw-bg-neutral-900/30"
      >
        <FileX class="tw-h-11 tw-w-11 tw-text-surface-300 dark:tw-text-neutral-600" />
        <p class="tw-mt-3 tw-text-sm tw-font-medium tw-text-surface-500 dark:tw-text-neutral-400">
          {{ t('requests.titles.no_attachment_found') }}
        </p>
      </div>
    </div>

    <input
      ref="fileInputRef"
      type="file"
      class="tw-hidden"
      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
      multiple
      @change="handleFilePickerChange"
    />

    <AttachmentDropzone
      v-if="editable && displayFiles.length && displayFiles.length < maxFiles"
      compact
      class="tw-mt-3 tw-shrink-0"
      :current-count="displayFiles.length"
      :disabled="disabled || uploading"
      @add="files => emit('add', files)"
      @error="message => emit('error', message)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Download, FileImage, FileStack, FileText, FileX, Maximize2 } from 'lucide-vue-next';
import AttachmentDropzone from './AttachmentDropzone.vue';
import type { AttachmentFile } from './types';
import { AlascoDocumentTabs, type AlascoDocumentTab } from '@/components/shared/tabs';
import { Button } from '@/components/ui/button';

interface Props {
  files?: AttachmentFile[];
  pendingFiles?: AttachmentFile[];
  editable?: boolean;
  disabled?: boolean;
  uploading?: boolean;
  maxFiles?: number;
  allowRemoveExisting?: boolean;
  // Detail views hide the "Documents" title so the doc tabs align with the
  // right-pane tabs; forms keep it (it lines up with the form section headers).
  showHeading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  files: () => [],
  pendingFiles: () => [],
  editable: false,
  disabled: false,
  uploading: false,
  // No cap by default — users can attach as many files as they need.
  maxFiles: Infinity,
  allowRemoveExisting: true,
  showHeading: true,
});

const emit = defineEmits<{
  add: [files: File[]];
  remove: [file: AttachmentFile];
  error: [message: string];
}>();

const { t } = useI18n();
const activeTab = ref('');
const previewContainerRef = ref<HTMLElement | HTMLElement[]>();
const fileInputRef = ref<HTMLInputElement>();

const displayFiles = computed(() => [...props.files, ...props.pendingFiles]);

const documentTabs = computed<AlascoDocumentTab[]>(() =>
  displayFiles.value.map(file => ({
    value: String(file.id),
    label: file.file_name || t('orders.titles.unnamed_file'),
    icon: isPdf(file) ? FileText : FileImage,
    closable: props.editable && (file.isPending || props.allowRemoveExisting),
  }))
);

const activeFile = computed(() =>
  displayFiles.value.find(file => String(file.id) === activeTab.value)
);

const activeFileUrl = computed(() => {
  if (!activeFile.value) return '';
  return activeFile.value.file || activeFile.value.previewUrl || '';
});

watch(
  displayFiles,
  files => {
    if (!files.length) {
      activeTab.value = '';
      return;
    }
    if (!files.some(file => String(file.id) === activeTab.value)) {
      activeTab.value = String(files[0].id);
    }
  },
  { immediate: true, deep: true }
);

const getPreviewUrl = (file: AttachmentFile) => file.previewUrl || file.file || '';
const isPdf = (file: AttachmentFile) => {
  const name = file.file_name || file.file || file.previewUrl || '';
  return name.toLowerCase().includes('.pdf');
};

const toggleFullscreen = () => {
  const container = Array.isArray(previewContainerRef.value)
    ? previewContainerRef.value[0]
    : previewContainerRef.value;
  container?.requestFullscreen?.();
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const handleFilePickerChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) {
    emit('add', Array.from(target.files));
    target.value = '';
  }
};

const handleTabClose = (value: string) => {
  const file = displayFiles.value.find(item => String(item.id) === value);
  if (file) {
    emit('remove', file);
  }
};
</script>
